import { NextSeo } from "next-seo";
import { GetStaticPropsContext } from "next";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { Navbar } from "~/components/Navbar";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    BlogPost,
    getAllPosts,
    getPostBySlug,
} from "~/modules/blog/api/sanity";
import { BlogIndividualPage } from "~/modules/blog/pages/individual";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { getBlogPostURL, Routes } from "~/routes";
import {
    NEXTJS_MISS_REVALIDATE_TIMEOUT,
    NEXTJS_REVALIDATE_TIMEOUT,
} from "~/server/consts";

// // // //

export async function getStaticPaths() {
    // Include archived posts so their individual pages remain routable, even
    // though they are hidden from the blog list and RSS feed.
    const paths = await getAllPosts({ includeArchived: true });
    return {
        paths: paths.map((post) => ({
            params: { slug: post.slug.current },
        })),
        fallback: "blocking",
    };
}

export async function getStaticProps(context: GetStaticPropsContext) {
    const slug = context.params?.slug as string;

    if (!slug) {
        return {
            notFound: true,
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    const post = await getPostBySlug(slug);

    // Render the 404 page when no post exists for this slug
    if (post === null) {
        return {
            notFound: true,
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    return {
        props: {
            post,
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function BlogPostPage(props: { post: BlogPost }) {
    const { post } = props;
    const title = post?.title || "";
    const SEO_DESCRIPTION = post?.excerpt || "";
    const canonical = getBlogPostURL({ slug: post?.slug.current || "" });

    return (
        <PageTrack route={Routes.blogIndividual}>
            <NextSeo
                title={title}
                canonical={addBaseUrl(canonical)}
                description={SEO_DESCRIPTION}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(canonical),
                    title: `RWA.xyz - ${title}`,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                }}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Blog",
                        href: Routes.blog,
                    },
                    {
                        name: title,
                        href: canonical,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.blogIndividual} user={user} />
                )}
            </UserFetcher>
            <SidebarLayout pathname={Routes.blog} showFooter>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Blog",
                            href: Routes.blog,
                            current: false,
                        },
                        {
                            name: title,
                            current: true,
                        },
                    ]}
                />
                <PageWrapper>
                    {/* <PageHeader title={title} subtitle={post?.excerpt || ""} /> */}

                    <BlogIndividualPage post={post} />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
